/**
 * PostHogProvider — boots the client SDK + identifies the signed-in user +
 * captures privacy-safe pageviews on every route change.
 *
 * Mount once near the top of the app tree (`app/layout.tsx`), inside the
 * `AuthProvider` so we can read `useAuth().user`.
 *
 * No-op when `NEXT_PUBLIC_POSTHOG_KEY` is empty.
 */

"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import {
    capturePageview,
    identifyUser,
    initPostHogClient,
    isPostHogEnabled,
    resetUser,
} from "@/lib/analytics/posthog.client";
import { hashUserId } from "@/lib/log/sanitize";
import { useAuth } from "@/context/AuthContext";

export function PostHogProvider({ children }: { children: React.ReactNode }) {
    const { user, isAuthenticated } = useAuth();
    const pathname = usePathname();

    useEffect(() => {
        initPostHogClient();
    }, []);

    // Identify / reset whenever the auth state changes. Identify uses the
    // hashed user id — never the raw email / oid.
    useEffect(() => {
        if (!isPostHogEnabled()) return;
        let cancelled = false;
        if (isAuthenticated && user?.id) {
            (async () => {
                const hashed = await hashUserId(user.id);
                if (cancelled) return;
                identifyUser(hashed);
            })();
        } else if (!isAuthenticated) {
            resetUser();
        }
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, user?.id]);

    // Manual pageview after URL-stripping — avoids leaking query params (e.g.
    // password-reset tokens / invite redemption codes) that PostHog's auto
    // pageview would capture.
    useEffect(() => {
        if (!pathname) return;
        capturePageview(pathname);
    }, [pathname]);

    return <>{children}</>;
}
